import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { JobTitleFormRule, JobTitleFormSchema } from "@core/form/form.rule";
import { useFormDefaultJobTitle } from "@core/form/form-default-job-title.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateJobTitle, 
  useQyGetJobTitleById, 
  useQyDeleteJobTitle 
} from "@core/query/job-title.query";
import { GetResponseJobTitleModel } from "@core/model/query-model-job-title";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";

export function useEditJobTitle() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultJobTitle();
  const navigate = useNavigate();
  const { mapUpdateJobTitle, mapDeleteJobTitle } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { jobTitleId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID JobTitle
  const handleGetApiSuccess = (data: GetResponseJobTitleModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
    }
  };
  const {
    data: jobTitleResponse,
    isLoading,
    isError: jobTitleError,
  } = useQyGetJobTitleById(jobTitleId || "", handleGetApiSuccess);
  const jobTitleData = jobTitleResponse?.data?.[0];

  // API UPDATE JobTitle
  const handleUpdateApiSuccess = () => {
    showSuccess("JobTitle updated successfully");
  };
  const { mutate: updateJobTitle, isLoading: isEditLoading } = useQyUpdateJobTitle(
    handleUpdateApiSuccess
  );

  // API DELETE JobTitle
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("JobTitle deleted successfully");
  };
  const { mutate: deleteJobTitle } = useQyDeleteJobTitle(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<JobTitleFormSchema>({
    defaultValues: getDefault(jobTitleData),
    resolver: zodResolver(JobTitleFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: JobTitleFormSchema) => {
    if (!jobTitleData) {
      throw new Error("No jobTitle data");
    }

    const formData = mapUpdateJobTitle(form, jobTitleData.code);
    updateJobTitle(formData);
  };
  const handleValidateError = (err: FieldErrors<JobTitleFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!jobTitleData) {
      throw new Error("No jobTitle data found");
    }

    const form = mapDeleteJobTitle(jobTitleData.code);
    deleteJobTitle(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    jobTitleId,
    jobTitleData,
    isLoading,
    jobTitleError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}